// file: src/TowingDashboard/DashboardLayout.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import CarIcon from "../Icons/CarIcon.jsx";
import UserIcon from "../Icons/UserIcon.jsx";
import { motion, AnimatePresence } from "framer-motion";
import {
  slideInFrom,
  fadeUp,
  staggerContainer,
  hoverLiftButton,
} from "../Animations/motionpresets.js";
import EmailIcon from "../Icons/EmailIcon.jsx";
import UserClassicIcon from "../Icons/UserClassicIcon.jsx";
import HeadsetIcon from "../Icons/HeadsetIcon.jsx";
import FloatingSupport from "./Overview/Components/FloatingSupport.jsx";
import Overview from "../TowingDashboard/Overview";

// --- brand colors (requested) ---
const BLUE = "#1271EB";
const BLUE_LIGHT = "#4D95F0";

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const hoverTimer = useRef(null);

  const nav = useNavigate();

  function handleLogout() {
    const goLogin = () =>
      nav("/towing/login", { replace: true, state: { fromLogout: true } });

    (async () => {
      try {
        // wipe both storages
        try {
          localStorage.clear();
        } catch {}
        try {
          sessionStorage.clear();
        } catch {}

        // quietly clear SW caches if any (doesn't reload)
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } finally {
        // SPA navigation (no refresh)
        goLogin();
      }
    })();
  }

  const auth = useMemo(() => {
    try {
      const user = JSON.parse(
        localStorage.getItem("auth_user") ||
          sessionStorage.getItem("auth_user") ||
          "null"
      );
      if (!user) return null;
      const email = user?.email || "user@example.com";
      const username = email.split("@")[0];
      return { email, username };
    } catch {
      return { email: "user@example.com", username: "user" };
    }
  }, []);

  // brand circle uses gradient (no layout change)
  const brandCircle =
    "inline-grid place-items-center rounded-xl flex-shrink-0 text-white h-8 w-8 md:h-12 md:w-12";
  const brandCircleStyle = {
    background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
    boxShadow: `0 12px 28px ${BLUE}33`,
  };

  const openSoon = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setUserOpen(true), 60);
  };
  const closeSoon = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setUserOpen(false), 120);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="flex">
        {/* Desktop sidebar */}
        <div className="hidden md:block flex-none">
          <Sidebar className="flex-none w-[320px] lg:w-[360px] xl:w-[400px] h-screen sticky top-0" />
        </div>

        <div
          className={`md:hidden fixed inset-0 z-40 ${
            mobileOpen ? "" : "pointer-events-none"
          }`}
        >
          <div
            className={`absolute inset-0 bg-black/40 transition-opacity ${
              mobileOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className={`absolute left-0 top-0 h-full w-[88vw] max-w-[420px] bg-white border-r border-slate-200 transition-transform ${
              mobileOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <Sidebar
              className="w-full h-full"
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>

        {/* Right content area */}
        <main className="flex-1 min-w-0">
          {/* Top bar */}
          <motion.header
            className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200"
            variants={slideInFrom("up", 18)}
            initial="hidden"
            animate="show"
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
              {/* Mobile menu */}
              <motion.button
                type="button"
                className="
                  md:hidden inline-flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-md
                  border border-white/60 text-slate-600 bg-white/60 backdrop-blur
                  hover:bg-white/80 transition
                "
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                whileTap={{ scale: 0.97 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.button>

              {/* User menu */}
              <div
                className="ml-auto relative"
                onMouseEnter={openSoon}
                onMouseLeave={closeSoon}
              >
                <motion.button
                  className={brandCircle}
                  style={brandCircleStyle}
                  aria-haspopup="true"
                  aria-expanded={userOpen}
                  aria-label="User menu"
                  title={`${auth?.username || "user"} (${auth?.email || ""})`}
                  {...hoverLiftButton}
                  onClick={() => setUserOpen((o) => !o)}
                >
                  <UserIcon className="w-4 h-4 md:w-6 md:h-6 " />
                </motion.button>

                <AnimatePresence>
                  {userOpen && (
                    <motion.div
                      key="usercard"
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 6, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 240,
                        damping: 22,
                      }}
                      className="
                        absolute right-0 top-full mt-2 w-72
                        rounded-2xl border border-white/70 bg-white/100 backdrop-blur
                        shadow-[0_20px_60px_rgba(67,56,202,.10),0_8px_24px_rgba(2,6,23,.06)]
                        p-3
                      "
                      onMouseEnter={openSoon}
                      onMouseLeave={closeSoon}
                    >
                      <span
                        className="absolute -top-1.5 right-3.5 h-3 w-3 rotate-45 bg-white/90 border-l border-t border-white/70"
                        aria-hidden="true"
                      />

                      {/* The three info rows use the blue gradient chip */}
                      <Row
                        icon={
                          <Chip>
                            <UserClassicIcon className="w-4 h-4" />
                          </Chip>
                        }
                        title="Username"
                        value={auth?.username || "user"}
                      />
                      <Row
                        icon={
                          <Chip>
                            <EmailIcon className="w-4 h-4" />
                          </Chip>
                        }
                        title="Email"
                        value={auth?.email || "user@example.com"}
                      />
                      <Row
                        icon={
                          <Chip>
                            <HeadsetIcon className="w-4 h-4" />
                          </Chip>
                        }
                        title="Customer Support"
                        value="support@yourspotrented.com"
                      />

                      <div className="mt-2 h-px bg-slate-200/70" />

                      <button
                        onClick={handleLogout}
                        className="
                          mt-2 w-full inline-flex items-center gap-2 rounded-xl px-3 py-2
                          text-sm font-medium text-slate-700 hover:text-slate-900
                          hover:bg-slate-50 transition cursor-pointer
                        "
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M15 12H3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M6 9l-3 3 3 3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M9 6V5a2 2 0 012-2h8a2 2 0 012 2v14a2 2 0 01-2 2h-8a2 2 0 01-2-2v-1"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.header>

          <div className="max-w-6xl mx-auto px-4 sm:mt-5 py-6">
            <Overview />
          </div>
          <FloatingSupport
            email="support@yourspotrented.com"
            phone="+1 (331) 248-5707"
          />
        </main>
      </div>
    </div>
  );
}

/* tiny gradient chip used in user dropdown; keeps your colors */
function Chip({ children }) {
  return (
    <span
      className="grid h-7 w-7 place-items-center rounded-full text-white shadow"
      style={{
        background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
        boxShadow: `0 8px 18px ${BLUE_LIGHT}55`,
      }}
    >
      {children}
    </span>
  );
}

function Row({ icon, title, value }) {
  return (
    <div className="flex items-start gap-3 rounded-xl px-2 py-2 hover:bg-slate-50/70 transition">
      {icon}
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {title}
        </div>
        <div className="text-sm text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}
